import { useTheme } from '@mui/material/styles';
import Mark from 'mark.js';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface SearchContextData {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  rootRef: React.RefObject<HTMLDivElement>;
}

const SearchContext = createContext<SearchContextData | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

const useHighlightText = (
  search: string,
  rootRef: React.RefObject<HTMLDivElement>
) => {
  const theme = useTheme();
  const searchRef = useRef(search);
  const isHighlightingRef = useRef(false);
  searchRef.current = search;

  useEffect(() => {
    const ensureHighlightCSS = () => {
      const styleId = 'highlight-style';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
          .highlight {
            background-color: ${theme.palette.warning[500]};
          }
        `;
        document.head.appendChild(style);
      }
    };

    const applyHighlight = () => {
      if (!rootRef.current || isHighlightingRef.current) return;

      isHighlightingRef.current = true;
      const instance = new Mark(rootRef.current);
      instance.unmark({
        done: () => {
          if (searchRef.current.length) {
            ensureHighlightCSS();
            instance.mark(searchRef.current, {
              element: 'span',
              className: 'highlight',
              separateWordSearch: false,
              acrossElements: false,
            });
          }
          // Small delay to ensure mark.js DOM changes are complete
          setTimeout(() => {
            isHighlightingRef.current = false;
          }, 10);
        },
      });
    };

    // Apply highlight initially and when search changes
    const timeoutId = setTimeout(applyHighlight, 0);

    // Watch for DOM changes and re-apply highlighting
    const observer = new MutationObserver(() => {
      if (searchRef.current.length && !isHighlightingRef.current) {
        setTimeout(applyHighlight, 0);
      }
    });

    if (rootRef.current) {
      observer.observe(rootRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [search, rootRef, theme]);
};

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [search, setSearch] = useState<string>('');
  const rootRef = useRef<HTMLDivElement>(null);

  useHighlightText(search, rootRef);

  const value = useMemo(() => ({ search, setSearch, rootRef }), [search]);

  return (
    <SearchContext.Provider value={value}>
      <div ref={rootRef}>{children}</div>
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
